/**
 * Author photo 3D mouse-tilt parallax (Webflow IX2 a-43, ANIMATIONS.md §2b).
 *
 * The circular `.author-picture-holder` tilts in 3D following the pointer
 * (rotationY -30..30 on X, rotationX 30..-30 on Y) while the inner
 * `.author-image` counter-shifts (xPercent/yPercent -4..4) for depth. Motion is
 * smoothed with gsap.quickTo and resets on mouseleave. The holder needs a
 * perspective for the rotateX/Y to read as 3D, applied here via gsap.set.
 *
 * Renders the author photo markup verbatim from detail_author.html; the
 * `.author-border-container` border-draw reveal is handled globally.
 */
import type { FC } from 'react'

import { useEffect, useRef } from 'react'

import { gsap } from '@/lib/gsap'

interface AuthorPhotoTiltProps {
    image: string
    name: string
}

export const AuthorPhotoTilt: FC<AuthorPhotoTiltProps> = ({ image, name }) => {
    const holderRef = useRef<HTMLDivElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)

    useEffect(() => {
        const holder = holderRef.current
        const img = imageRef.current
        if (!holder || !img) return

        const ctx = gsap.context(() => {
            gsap.set(holder, { transformPerspective: 800, transformStyle: 'preserve-3d' })

            const rotateY = gsap.quickTo(holder, 'rotationY', { duration: 0.5, ease: 'power2.out' })
            const rotateX = gsap.quickTo(holder, 'rotationX', { duration: 0.5, ease: 'power2.out' })
            const imageX = gsap.quickTo(img, 'xPercent', { duration: 0.5, ease: 'power2.out' })
            const imageY = gsap.quickTo(img, 'yPercent', { duration: 0.5, ease: 'power2.out' })

            const onMove = (event: MouseEvent) => {
                const rect = holder.getBoundingClientRect()
                const nx = (event.clientX - rect.left) / rect.width
                const ny = (event.clientY - rect.top) / rect.height

                rotateY(gsap.utils.mapRange(0, 1, -30, 30, nx))
                rotateX(gsap.utils.mapRange(0, 1, 30, -30, ny))
                imageX(gsap.utils.mapRange(0, 1, -4, 4, nx))
                imageY(gsap.utils.mapRange(0, 1, -4, 4, ny))
            }

            const onLeave = () => {
                rotateY(0)
                rotateX(0)
                imageX(0)
                imageY(0)
            }

            holder.addEventListener('mousemove', onMove)
            holder.addEventListener('mouseleave', onLeave)

            return () => {
                holder.removeEventListener('mousemove', onMove)
                holder.removeEventListener('mouseleave', onLeave)
            }
        }, holder)

        return () => ctx.revert()
    }, [])

    return (
        <div className='author-profile-holder'>
            <div ref={holderRef} className='author-picture-holder'>
                <div className='author-profile-picture'>
                    <img ref={imageRef} alt={name} loading='lazy' src={image} className='author-image' />
                </div>
            </div>
        </div>
    )
}
